import { Save, Palette, Crown } from "lucide-react";

const COLOR_PRESETS = [
  { name: "Ocean", primary: "#0c4a6e", secondary: "#0ea5e9", accent: "#38bdf8", text: "#ffffff" },
  { name: "Forest", primary: "#14532d", secondary: "#22c55e", accent: "#4ade80", text: "#ffffff" },
  { name: "Sunset", primary: "#7c2d12", secondary: "#f97316", accent: "#fb923c", text: "#ffffff" },
  { name: "Purple", primary: "#581c87", secondary: "#a855f7", accent: "#c084fc", text: "#ffffff" },
  { name: "Midnight", primary: "#1e1b4b", secondary: "#4f46e5", accent: "#818cf8", text: "#ffffff" },
  { name: "Coffee", primary: "#422006", secondary: "#a16207", accent: "#ca8a04", text: "#ffffff" },
];

export default function DashboardBrandingTab({
  selectedVenue,
  selectedVenueIsPremium,
  editForm,
  setEditForm,
  handleSave,
  saving,
  handleSubscribe,
  pricing
}) {
  if (!selectedVenueIsPremium) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Palette className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Branding</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
            Make your venue page stand out with custom brand colors. Premium venues get a unique look that matches their brand.
          </p>
          
          {/* Greyed-out preview */}
          <div className="max-w-sm mx-auto mb-6 opacity-40 pointer-events-none">
            <div 
              className="rounded-xl p-4 transition-all"
              style={{ background: 'linear-gradient(135deg, #94a3b8, #cbd5e1)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-400 flex items-center justify-center font-bold text-lg text-white">
                  {selectedVenue?.name?.charAt(0) || 'V'}
                </div>
                <div>
                  <div className="font-semibold text-white text-sm">{selectedVenue?.name || 'Your Venue'}</div>
                  <div className="text-xs text-white/70">Preview</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              <div className="h-8 rounded bg-gray-300"></div>
              <div className="h-8 rounded bg-gray-200"></div>
              <div className="h-8 rounded bg-gray-400"></div>
              <div className="h-8 rounded bg-gray-300"></div>
            </div>
          </div>
          
          <button
            onClick={() => handleSubscribe("premium_annual", selectedVenue?.id)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
            data-testid="branding-upgrade-btn"
          >
            <Crown className="w-5 h-5" />
            Upgrade to Premium - £{pricing?.premium_annual || 29}/year
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-100">
      <h3 className="font-medium text-gray-900 mb-2">Custom Color Scheme</h3>
      <p className="text-sm text-gray-500 mb-6">
        Personalize your venue page with your brand colors. These colors will be applied to your venue's detail page.
      </p>
      
      {/* Color Preview */}
      <div 
        className="rounded-xl p-4 mb-6 transition-all"
        style={{
          background: `linear-gradient(135deg, ${editForm.branding?.primary_color || '#1e3a5f'}, ${editForm.branding?.secondary_color || '#57c5b6'})`,
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg"
            style={{ 
              backgroundColor: editForm.branding?.accent_color || '#159895',
              color: editForm.branding?.text_color || '#ffffff'
            }}
          >
            {selectedVenue.name?.charAt(0) || 'V'}
          </div>
          <div>
            <h4 
              className="font-semibold"
              style={{ color: editForm.branding?.text_color || '#ffffff' }}
            >
              {selectedVenue.name}
            </h4>
            <p 
              className="text-sm opacity-80"
              style={{ color: editForm.branding?.text_color || '#ffffff' }}
            >
              Preview of your brand colors
            </p>
          </div>
        </div>
        <button 
          className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{ 
            backgroundColor: editForm.branding?.accent_color || '#159895',
            color: editForm.branding?.text_color || '#ffffff'
          }}
        >
          Sample Button
        </button>
      </div>
      
      {/* Color Inputs */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {[
          { key: "primary_color", label: "Primary Color", default: "#1e3a5f" },
          { key: "secondary_color", label: "Secondary Color", default: "#57c5b6" },
          { key: "accent_color", label: "Accent Color", default: "#159895" },
          { key: "text_color", label: "Text Color", default: "#ffffff" },
        ].map(({ key, label, default: defaultVal }) => (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={editForm.branding?.[key] || defaultVal}
                onChange={(e) => setEditForm({
                  ...editForm,
                  branding: { ...editForm.branding, [key]: e.target.value }
                })}
                className="w-12 h-10 rounded cursor-pointer border border-gray-200"
              />
              <input
                type="text"
                value={editForm.branding?.[key] || defaultVal}
                onChange={(e) => setEditForm({
                  ...editForm,
                  branding: { ...editForm.branding, [key]: e.target.value }
                })}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono"
                placeholder={defaultVal}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Preset Colors */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Quick Presets</label>
        <div className="flex flex-wrap gap-2">
          {COLOR_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => setEditForm({
                ...editForm,
                branding: {
                  primary_color: preset.primary,
                  secondary_color: preset.secondary,
                  accent_color: preset.accent,
                  text_color: preset.text
                }
              })}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:scale-105"
              style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.secondary})` }}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>
      
      <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
        <Save className="w-4 h-4" />
        {saving ? "Saving..." : "Save Branding"}
      </button>
    </div>
  );
}
